import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
    ViewChild
} from '@angular/core';

import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { map, take, tap } from 'rxjs/operators';

import { CommonModule } from '@angular/common';
import { KEYS, TreeModule } from '@huntsman-cancer-institute/angular-tree-component';
import { flatMapDeep } from 'lodash-es';
import { BaseContainer } from '../../../core/common/containers/base.abstract.container';
import { PaginatedResults } from '../../../overviews/generic-overview/components/pagination/entities/paginated-results.entity';
import { ShTabFocusDisableDirective } from '../../directives/tab-disabler/tab-disable.directive';
import { ShIconModule } from '../icon/icon.module';
import {
    ITreeOptions,
    ITreeViewerNode,
    NodeOffset,
    NodeTypes,
    TREE_ACTIONS,
    TreeComponent,
    TreeEvent,
    TreeModel,
    TreeNode,
    TreeViewerNode
} from './entities/tree-viewer-node.entity';

export { ITreeViewerNode, TreeViewerNode } from './entities/tree-viewer-node.entity';
@Component({
    standalone: true,
    selector: 'sh-tree-viewer',
    templateUrl: './tree-viewer.component.html',
    styleUrls: ['./tree-viewer.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonModule, ShIconModule, TreeModule, ShTabFocusDisableDirective]
})
export class ShTreeViewerComponent<T> extends BaseContainer implements OnInit, OnDestroy {
    public treeOptions: ITreeOptions = {
        actionMapping: {
            mouse: {
                click: (tree: TreeModel, node: TreeNode, $event: Event) => {
                    this.onClickNode(tree, node, $event);
                },
                checkboxClick: (tree: TreeModel, node: TreeNode, $event: Event) => {
                    this.onClickNode(tree, node, $event);
                }
            },
            keys: {
                [KEYS.RIGHT]: TREE_ACTIONS.DRILL_DOWN,
                [KEYS.LEFT]: TREE_ACTIONS.DRILL_UP,
                [KEYS.DOWN]: TREE_ACTIONS.NEXT_NODE,
                [KEYS.UP]: TREE_ACTIONS.PREVIOUS_NODE,
                [KEYS.SPACE]: (tree: TreeModel, node: TreeNode, $event: Event) => {
                    this.onClickNode(tree, node, $event);
                },
                [KEYS.ENTER]: (tree: TreeModel, node: TreeNode, $event: Event) => {
                    this.onClickNode(tree, node, $event);
                }
            }
        },
        allowDrag: false,
        allowDrop: false,
        useVirtualScroll: false,
        levelPadding: 10,
        animateExpand: true,
        scrollOnActivate: true,
        animateSpeed: 30,
        animateAcceleration: 1.2,
        useTriState: false
    };

    public nodeTypes: typeof NodeTypes = NodeTypes;

    public treeNodes$!: Observable<TreeViewerNode<T>[]>;
    public roots$: BehaviorSubject<TreeViewerNode<T>[]> = new BehaviorSubject<TreeViewerNode<T>[]>([]);
    public hideRootNodes$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    private readonly selectedNodes$: BehaviorSubject<TreeViewerNode<T>[]> = new BehaviorSubject<TreeViewerNode<T>[]>(
        []
    );

    private readonly deselectedNodes$: BehaviorSubject<TreeViewerNode<T>[]> = new BehaviorSubject<TreeViewerNode<T>[]>(
        []
    );

    public readonly isFocused$: BehaviorSubject<boolean> = new BehaviorSubject(false);

    private nodesOffset: NodeOffset = {};
    private loadChildrenFct?: (
        node: TreeViewerNode<T>,
        startIndex: number
    ) => Observable<PaginatedResults<TreeViewerNode<T>>>;

    @ViewChild('tree') tree!: TreeComponent;
    @ViewChild('tree', { read: ElementRef }) treeElement!: ElementRef<HTMLElement>;

    @Input() set nodes(treeNodes: TreeViewerNode<T>[]) {
        this.roots$.next(treeNodes);
    }

    @Input() set hideRootNodes(hideRootNodes: boolean) {
        this.hideRootNodes$.next(hideRootNodes);
    }

    @Input() multiple = false;

    @Input() set getChildrenFct(
        fct: (node: TreeViewerNode<T>, startIndex: number) => Observable<PaginatedResults<TreeViewerNode<T>>>
    ) {
        this.initDynamicLoadingChildren(fct);
    }

    @Input() loadMoreMsg = 'Load more';
    @Input() loadingMsg = 'Loading more...';

    @Output() selectedNodes: EventEmitter<TreeViewerNode<T>[]> = new EventEmitter<TreeViewerNode<T>[]>();
    @Output() deselectedNodes: EventEmitter<TreeViewerNode<T>[]> = new EventEmitter<TreeViewerNode<T>[]>();
    @Output() updatedNodes: EventEmitter<TreeViewerNode<T>[]> = new EventEmitter<TreeViewerNode<T>[]>();

    constructor(private readonly cd: ChangeDetectorRef) {
        super();
    }

    ngOnInit(): void {
        this.initTreeNodes();

        document.addEventListener('keyup', this.tabListener.bind(this));
    }

    private tabListener(e: KeyboardEvent): void {
        if (e.code === 'Tab') {
            this.reCheckFocus();
        }
    }

    ngOnDestroy(): void {
        document.removeEventListener('keyup', this.tabListener);

        this.destroy$.next();
        this.destroy$.complete();

        this.roots$.complete();
        this.hideRootNodes$.complete();
        this.selectedNodes$.complete();
        this.deselectedNodes$.complete();
    }

    private initTreeNodes(): void {
        this.treeNodes$ = combineLatest([this.roots$, this.hideRootNodes$]).pipe(
            map(([rootNodes, hideRootNodes]) =>
                rootNodes.map(rootNode => ({
                    ...rootNode,
                    isRoot: true,
                    isExpanded: hideRootNodes
                }))
            )
        );
    }

    private initDynamicLoadingChildren(
        getChildrenFct: (node: TreeViewerNode<T>, startIndex: number) => Observable<PaginatedResults<TreeViewerNode<T>>>
    ): void {
        this.loadChildrenFct = getChildrenFct;

        this.treeOptions.getChildren = (node: ITreeViewerNode<T>) => {
            if (!node.id || !this.loadChildrenFct) {
                return;
            }

            this.loading$.next(true);
            return this.loadChildrenFct(node, this.getCurrentOffset(node.id))
                .pipe(
                    map(results => this.processPaginatedResults(results, node.id)),
                    tap(() => this.loading$.next(false))
                )
                .toPromise();
        };
    }

    private getCurrentOffset(parentNodeId?: number | string): number {
        if (this.nodesOffset && parentNodeId !== undefined && this.nodesOffset[parentNodeId]) {
            return this.nodesOffset[parentNodeId].offset;
        } else {
            return 0;
        }
    }

    private processPaginatedResults(
        paginatedResults: PaginatedResults<TreeViewerNode<T>>,
        parentNodeId: number | string
    ): TreeViewerNode<T>[] {
        const currentOffset = this.getCurrentOffset(parentNodeId);

        // If there are more nodes to load, update offset and add a load-more node to nodes
        if (currentOffset + paginatedResults.results.length < paginatedResults.paginationInfo.totalNumberOfResults) {
            this.addOrUpdateNodeOffset(parentNodeId, paginatedResults.results.length);
            paginatedResults.results.push(
                this.getLoadMoreNode(
                    parentNodeId,
                    this.nodesOffset[parentNodeId].offset,
                    paginatedResults.paginationInfo.totalNumberOfResults
                )
            );
        }

        // Filter out already existing nodes

        return paginatedResults.results.filter(node => !this.tree.treeModel.getNodeById(node.id));
    }

    private addOrUpdateNodeOffset(nodeId: number | string, offset: number) {
        if (this.nodesOffset[nodeId]) {
            this.nodesOffset[nodeId].offset += offset;
        } else {
            this.nodesOffset[nodeId] = { offset };
        }
    }

    private getLoadMoreNode(parentId: number | string, loaded: number, totalItems: number): any {
        const remaining = totalItems - loaded;

        return {
            type: NodeTypes.LoadMore,
            name: `${this.loadMoreMsg} (${remaining})`,
            id: parentId + '-load-more',
            hasChildren: false
        };
    }

    private loadMoreChildren(node: TreeNode): void {
        if (node.data.name === this.loadingMsg || !this.loadChildrenFct) {
            return;
        }
        node.data.name = this.loadingMsg;
        const parentNode = node.parent.data;

        this.loadChildrenFct(parentNode, this.getCurrentOffset(parentNode.id))
            .pipe(
                take(1),
                map(response => this.processPaginatedResults(response, parentNode.id))
            )
            .subscribe(nodes => {
                // remove load node
                // try fast way of popping last entry before slow filter
                if (node.parent.data.children[node.parent.data.children.length - 1].id === node.data.id) {
                    node.parent.data.children.pop();
                } else {
                    node.parent.data.children = node.parent.data.children.filter(
                        (child: TreeNode) => child.id !== node.data.id
                    );
                }
                node.parent.data.children = [...node.parent.data.children, ...nodes];

                this.tree.treeModel.update();
                this.cd.detectChanges();
            });
    }

    private retriggerNodes(nodes: TreeViewerNode<T>[], activeOnly: boolean = false): void {
        const getChildren: any = (child: TreeViewerNode<T>) => {
            if (!child.children?.length) {
                return child;
            }
            return [child, flatMapDeep(child.children, getChildren)];
        };

        let flattenedNodes: TreeViewerNode<T>[] = flatMapDeep(nodes, getChildren);
        flattenedNodes = flattenedNodes.filter(node => !node.disabled);

        if (activeOnly) flattenedNodes = flattenedNodes.filter(node => node?.isActive);

        for (const node of flattenedNodes) {
            const treeNode: TreeNode = this.tree.treeModel.getNodeById(node.id);

            if (this.multiple) {
                treeNode.setIsSelected(node.isActive);
            }
        }

        if (!this.multiple) {
            const iActiveNode: Partial<ITreeViewerNode<T>> | undefined = flattenedNodes.find(node => node.isActive);

            // Select and unselect the root to make sure nothing is selected
            if (!iActiveNode) {
                const root: TreeNode | undefined = this.tree.treeModel.getNodeById(nodes[0].id);
                root?.setIsActive(true);
                root?.setIsActive(false);
                return;
            }

            const activeNode: TreeNode | undefined = this.tree.treeModel.getNodeById(iActiveNode?.id);
            activeNode?.setIsActive(true);
        }
    }

    private onClickNode(tree: TreeModel, node: TreeNode, event: Event): void {
        event.stopImmediatePropagation();

        if (!node) {
            return;
        }

        if (node.data.type === NodeTypes.LoadMore) {
            return this.loadMoreChildren(node);
        }

        if (!node.data.disabled && !this.multiple) {
            if (node.isActive) {
                this.onDeactivatedNode(node.data, true);
            } else {
                this.onActivatedNode(node.data, true);
            }
            node.setIsActive(!node.isActive);
        }

        if (!node.data.disabled && this.multiple) {
            if (node.isSelected) {
                this.removeSelectedNode(node.data, true);
            } else {
                this.addSelectedNode(node.data, true);
            }
            node.setIsSelected(!node.isSelected);
        }
    }

    public onEvent(event: TreeEvent) {
        switch (event.eventName) {
            case 'select':
                this.addSelectedNode(event.node.data);
                break;
            case 'deselect':
                this.removeSelectedNode(event.node.data);
                break;
            case 'activate':
                this.onActivatedNode(event.node.data);
                break;
            case 'deactivate':
                this.onDeactivatedNode(event.node.data);
                break;
            case 'initialized':
            case 'updateData':
                this.retriggerNodes(event.treeModel.nodes);
                this.sendNodeUpdates(event.treeModel.nodes);
                break;
            case 'loadNodeChildren':
                this.retriggerNodes(event.treeModel.nodes, true);
                this.sendNodeUpdates(event.treeModel.nodes);
                break;
            case 'focus':
                this.onFocusNode(event);
                break;
            default:
                return;
        }
    }

    /**
     * Internal scrolling doesn't play nice with our dialog, so we do it ourselves
     * @param event
     */
    private onFocusNode(event: TreeEvent): void {
        const xpath = `//*[contains(@class, 'node-content-wrapper') and .//span[text()='${event.node.data.name}']]`;
        const htmlNode: HTMLElement = document.evaluate(
            xpath,
            document,
            null,
            XPathResult.FIRST_ORDERED_NODE_TYPE,
            null
        ).singleNodeValue as HTMLElement;
        if (htmlNode) {
            htmlNode.scrollIntoView(false);
        }
    }

    private addSelectedNode(node: TreeViewerNode<T>, withEmit: boolean = false) {
        const currentValues: TreeViewerNode<T>[] = this.selectedNodes$.value;
        const isAlreadyAdded: boolean = currentValues.some(value => value.id === node.id);

        if (!isAlreadyAdded) {
            const updatedValues: TreeViewerNode<T>[] = [...currentValues, node];

            this.selectedNodes$.next(updatedValues);

            if (withEmit) {
                this.selectedNodes.next(updatedValues);
            }
        }
    }

    private removeSelectedNode(node: TreeViewerNode<T>, withEmit: boolean = false) {
        const currentValues: TreeViewerNode<T>[] = this.selectedNodes$.value;
        const updatedValues: TreeViewerNode<T>[] = currentValues.filter(value => value.id !== node.id);

        this.deselectedNodes$.next([node]);
        this.selectedNodes$.next(updatedValues);

        if (withEmit) {
            this.deselectedNodes.next([node]);
            this.selectedNodes.next(updatedValues);
        }
    }

    private onActivatedNode(node: TreeViewerNode<T>, withEmit: boolean = false) {
        const currentValues: TreeViewerNode<T>[] = this.selectedNodes$.value;
        const isAlreadyActive: boolean = currentValues.some(value => value.id === node.id);

        if (!this.multiple && !isAlreadyActive) {
            this.selectedNodes$.next([node]);

            if (withEmit) this.selectedNodes.next([node]);
        }
    }

    private onDeactivatedNode(node: TreeViewerNode<T>, withEmit: boolean = false) {
        const currentValues: TreeViewerNode<T>[] = this.selectedNodes$.value;
        const isNodeStillActive: boolean = currentValues.some(value => value.id === node.id);

        if (!this.multiple && isNodeStillActive) {
            this.selectedNodes$.next([]);

            if (withEmit) this.selectedNodes.next([]);
        }
    }

    public reCheckFocus(): void {
        setTimeout(() => {
            if (this.treeElement && document.activeElement === this.treeElement.nativeElement) {
                this.isFocused$.next(true);
            } else if (this.isFocused$.value) {
                this.isFocused$.next(false);
            }
        }, 0);
    }

    private sendNodeUpdates(updatedNodes: TreeViewerNode<T>[]) {
        this.updatedNodes.next(updatedNodes);
    }
}
